import { Stack } from "@mui/material";
import { MprcMessageBox, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonCancel, ButtonClose } from "components/buttons";
import { MprcButton } from "components/MprcButton";
import { StandardAccountResendConfirmationStatus } from "generated/CoreApi_v1";
import { useSnackbar } from 'notistack';
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router";
import { RootPaths } from "routes/RootRouter";
import { AccountRenewEmail } from "views/public/LoginPage/AccountRenewEmail";

////////////////////////////////////////////////////////////////////////////////

type RenderNotConfirmedProps = {
    authValue: string,
    onClose: () => void;
};

export const AccountNotConfirmed: React.FC<RenderNotConfirmedProps> = ({ authValue, onClose }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [renew, setRenew] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>(undefined);
    const api = useCoreApi();
    const navigate = useNavigate();

    ///
    const handleResendConfirmation = () => {
        setSubmitting(true);
        api.v1.account.standardAccountResendConfirmation({ authFieldValue: authValue, securityStamp: null })
            .then((response) => {
                var result = response.data;

                switch (result.status) {
                    case StandardAccountResendConfirmationStatus.Done:
                        enqueueSnackbar(t("Auth.AccountNotConfirmed.DoneMessage"), { variant: "success" });
                        onClose();
                        break;
                    case StandardAccountResendConfirmationStatus.EmailNotSend:
                        enqueueSnackbar(t("Auth.AccountNotConfirmed.EmailNotSendMessage"), { variant: "error" });
                        break;
                    case StandardAccountResendConfirmationStatus.AlreadyConfirmed:
                        enqueueSnackbar(t("Auth.AccountNotConfirmed.AlreadyConfirmedMessage"), { variant: "error" });
                        break;
                    case StandardAccountResendConfirmationStatus.NotFound:
                        enqueueSnackbar(t("Auth.Common.AccountNotFoundMessage"), { variant: "error" });
                        break;
                }

                setSubmitting(false);
            })
            .catch(e => {
                setViewError(e);
                setSubmitting(false);
            });
    };

    if (viewError)
        return (
            <MprcRequestErrorReport
                error={viewError}
                action={<ButtonClose onClick={() => navigate(RootPaths.Login, { replace: true })} />}
            />
        );

    return renew
        ? (
            <AccountRenewEmail
                authValue={authValue}
                onClose={
                    (result) => {
                        if (result == undefined) setRenew(false);
                        else onClose();
                    }
                }
            />
        )
        : (
            <MprcMessageBox
                size="medium"
                variant="warning"
                message={
                    <>
                        <p>{t("Auth.AccountNotConfirmed.AccountMessageL1")}</p>
                        <p>{t("Auth.AccountNotConfirmed.AccountMessageL2")}</p>
                    </>
                }
                onClose={onClose}
                action={
                    <Stack direction="row" spacing={1} justifyContent="center">
                        <ButtonCancel
                            variant="contained"
                            disabled={submitting}
                            text="Fechar"
                            onClick={onClose}
                        />
                        <MprcButton
                            variant="contained"
                            color="warning"
                            disabled={submitting}
                            onClick={handleResendConfirmation}
                        >
                            {t("Auth.AccountNotConfirmed.ButtonResend")}
                            {submitting && <MprcRequestIndicator />}
                        </MprcButton>
                        <MprcButton
                            variant="contained"
                            color="warning"
                            onClick={() => setRenew(true)}
                        >
                            {t("Auth.AccountNotConfirmed.ButtonChangeEmail")}
                        </MprcButton>
                    </Stack>
                }
            />
        );
};
